import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Check, QrCode, Monitor, Palette, Shield,
  ArrowRight, MessageSquare, BarChart2, Zap, ChevronRight, Gift
} from 'lucide-react';

const TESTIMONIALS = [
  {
    name: 'Sarah M.',
    business: 'Bloom Floral Studio',
    text: 'I went from zero reviews on my site to 40+ in the first month. My customers trust me more than ever.',
    stars: 5,
  },
  {
    name: 'James T.',
    business: 'Peak Auto Detailing',
    text: 'Super easy to set up. I printed the QR code and taped it to my counter. Reviews started coming in same day.',
    stars: 5,
  },
  {
    name: 'Maria L.',
    business: 'Casa de Tacos',
    text: 'My Squarespace site looks so much more professional now. Customers actually read the reviews before ordering.',
    stars: 5,
  },
];

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'Collect Testimonials Effortlessly',
    desc: 'Send customers a simple link or QR code. They leave a review in seconds — no app, no account needed.',
  },
  {
    icon: Monitor,
    title: 'Embed on Any Website',
    desc: 'One line of code adds a beautiful testimonial wall to Wix, Squarespace, WordPress, Shopify, or plain HTML.',
  },
  {
    icon: Palette,
    title: '20 Beautiful Themes',
    desc: 'Choose from light, dark, bold, neon, luxury, aurora and more. Including 5 dynamic mouse-reactive themes.',
  },
  {
    icon: QrCode,
    title: 'Built-in QR Code Generator',
    desc: 'Print QR codes on receipts, business cards, or in-store signage to collect reviews anywhere.',
  },
  {
    icon: BarChart2,
    title: 'Real-Time Dashboard',
    desc: 'Review, approve, and manage every testimonial from one clean dashboard. You stay in control.',
  },
  {
    icon: Shield,
    title: 'No Technical Skills Needed',
    desc: 'Built for small business owners, not developers. If you can copy and paste, you can use TestimonialWall.',
  },
];

const STEPS = [
  { number: '01', title: 'Create your account', desc: 'Sign up in under a minute with just your email.' },
  { number: '02', title: 'Share your link', desc: 'Send your unique survey link or QR code to customers.' },
  { number: '03', title: 'Embed your wall', desc: 'Paste one line of code on your website and you\'re live.' },
];

const TABS = ['Home', 'Features', 'How It Works', 'Pricing'];

export default function Home() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Home');

  return (
    <div className="min-h-screen bg-white font-sans flex flex-col">

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo — TW monogram */}
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => setActiveTab('Home')}>
            <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs tracking-tight">TW</span>
            </div>
            <span className="font-bold text-gray-900 text-lg">TestimonialWall</span>
          </div>

          {/* Tabs — hidden on small screens */}
          <div className="hidden md:flex items-center gap-1">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Auth buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium transition px-3 py-1.5"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 rounded-lg transition shadow-sm shadow-violet-200"
            >
              Get Started Free
            </button>
          </div>
        </div>

        {/* Mobile tabs */}
        <div className="md:hidden flex border-t border-gray-100 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 min-w-max px-4 py-2.5 text-xs font-medium transition whitespace-nowrap ${
                activeTab === tab
                  ? 'text-violet-700 border-b-2 border-violet-600'
                  : 'text-gray-500'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Tab Content ── */}
      <div className="flex-1">

        {/* ── HOME ── */}
        {activeTab === 'Home' && (
          <div>
            {/* Free banner */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 py-2.5 px-6 text-center">
              <p className="text-white text-sm font-semibold flex items-center justify-center gap-2">
                <Gift className="w-4 h-4" />
                🎉 Free right now — no credit card required!
              </p>
            </div>

            {/* Hero */}
            <section className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-white to-indigo-50 pt-20 pb-24 px-6">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-violet-100 rounded-full blur-3xl opacity-30 pointer-events-none" />
              <div className="relative max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-6">
                  <Zap className="w-3.5 h-3.5" />
                  The easiest way to collect customer reviews
                </div>
                <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
                  Turn happy customers into{' '}
                  <span className="text-violet-600">social proof</span>{' '}
                  that sells
                </h1>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
                  TestimonialWall lets you collect real customer reviews and display them beautifully on your website — no developers, no hassle.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/signup')}
                    className="flex items-center gap-2 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl text-lg transition shadow-lg shadow-violet-200"
                  >
                    Start Collecting Reviews — Free
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-sm text-gray-400">Free right now · No credit card needed</p>
                </div>

                {/* Sample testimonial cards */}
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  {TESTIMONIALS.map((t, i) => (
                    <div key={i} className="bg-white rounded-2xl p-5 shadow-md shadow-gray-100 border border-gray-100 text-left">
                      <div className="flex gap-0.5 mb-3">
                        {Array.from({ length: t.stars }).map((_, j) => (
                          <Star key={j} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed mb-4">"{t.text}"</p>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.business}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Quick feature highlights */}
            <section className="py-16 px-6 bg-white">
              <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
                {[
                  { icon: MessageSquare, title: 'Collect Reviews', desc: 'Share a link or QR code. Customers review in seconds.' },
                  { icon: Monitor, title: 'Embed Anywhere', desc: 'Works on Wix, Squarespace, WordPress, Shopify & more.' },
                  { icon: Palette, title: '20 Themes', desc: 'Beautiful themes including 5 dynamic animated ones.' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-6 bg-gradient-to-br from-violet-600 to-indigo-600">
              <div className="max-w-2xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-white mb-4">Start building trust today — it's free</h2>
                <p className="text-violet-200 text-lg mb-8">Join businesses already using TestimonialWall to turn happy customers into their best marketing.</p>
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-violet-700 font-bold rounded-xl text-lg hover:bg-violet-50 transition shadow-lg"
                >
                  Create Your Free Account <ArrowRight className="w-5 h-5" />
                </button>
                <p className="text-violet-300 text-sm mt-4">No credit card required</p>
              </div>
            </section>
          </div>
        )}

        {/* ── FEATURES ── */}
        {activeTab === 'Features' && (
          <section className="py-20 px-6 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Everything you need</h2>
                <p className="text-gray-500 text-lg">One simple tool that does it all.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {FEATURES.map((f, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                    <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <f.icon className="w-5 h-5 text-violet-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-12">
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition shadow-lg shadow-violet-200"
                >
                  Get Started Free <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── HOW IT WORKS ── */}
        {activeTab === 'How It Works' && (
          <section className="py-20 px-6 bg-white">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Up and running in minutes</h2>
                <p className="text-gray-500 text-lg">No technical skills required.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {STEPS.map((s, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className="text-6xl font-black text-violet-100 mb-3">{s.number}</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                    {i < STEPS.length - 1 && (
                      <div className="hidden md:block mt-4">
                        <ChevronRight className="w-6 h-6 text-gray-300 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Visual demo */}
              <div className="mt-16 bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <h3 className="text-center font-bold text-gray-900 mb-6">What your customers see</h3>
                <div className="max-w-sm mx-auto bg-white rounded-xl shadow-md p-6 border border-gray-100">
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-1">How was your experience?</p>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className="w-6 h-6 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                  <textarea
                    readOnly
                    value="Amazing service! Would recommend to everyone."
                    className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg p-3 resize-none"
                    rows={2}
                  />
                  <button className="mt-3 w-full py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg">
                    Submit Review
                  </button>
                </div>
              </div>

              <div className="text-center mt-12">
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition shadow-lg shadow-violet-200"
                >
                  Get Started Now — Free <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── PRICING ── */}
        {activeTab === 'Pricing' && (
          <section className="py-20 px-6 bg-white">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-4">
                  <Gift className="w-4 h-4" />
                  🎉 Free right now!
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Pricing</h2>
                <p className="text-gray-500 text-lg">Free for now. Prices will go live soon — sign up while it's free.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Starter */}
                <div className="bg-white border-2 border-emerald-300 rounded-2xl p-8 shadow-lg shadow-emerald-50 relative">
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-full text-xs font-bold">
                      <Gift className="w-3 h-3" /> Free Right Now
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold mb-6">
                    Starter
                  </div>
                  {/* Free + strikethrough */}
                  <div className="flex items-end gap-2 mb-1">
                    <span className="text-5xl font-black text-emerald-600">FREE</span>
                    <span className="text-gray-300 text-xl mb-1 line-through font-bold">$9.99/mo</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">Free right now. Normally <span className="line-through text-gray-400">$9.99/month</span>.</p>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Unlimited testimonials',
                      'Embeddable widget for any website',
                      '10 professional themes',
                      'QR code generator',
                      'Real-time moderation dashboard',
                    ].map(item => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Check className="w-2.5 h-2.5 text-emerald-600" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition shadow-sm"
                  >
                    Get Started Free
                  </button>
                </div>

                {/* Premium */}
                <div className="bg-white border-2 border-violet-200 rounded-2xl p-8 relative opacity-75">
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-violet-400 text-white rounded-full text-xs font-bold">
                      Coming soon
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold mb-6">
                    <Star className="w-3 h-3 fill-violet-500 text-violet-500" />
                    Premium
                  </div>
                  <div className="flex items-end gap-2 mb-1">
                    <span className="text-5xl font-black text-emerald-600">FREE</span>
                    <span className="text-gray-300 text-xl mb-1 line-through font-bold">$14.99/mo</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">Free right now. Normally <span className="line-through text-gray-400">$14.99/month</span>.</p>
                  <ul className="space-y-3 mb-8">
                    {[
                      'Everything in Starter',
                      '20 themes including animated',
                      '5 dynamic mouse-reactive themes',
                      'Priority support',
                      'All future premium features',
                    ].map(item => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-gray-700">
                        <div className="w-4 h-4 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                          <Check className="w-2.5 h-2.5 text-violet-600" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition shadow-sm shadow-violet-200"
                  >
                    Get Started Free
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-center text-sm text-gray-400 mt-6">Free right now. No credit card needed.</p>
            </div>
          </section>
        )}
      </div>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-violet-500 rounded-md flex items-center justify-center">
              <span className="text-white font-black text-[8px] tracking-tight">TW</span>
            </div>
            <span className="text-gray-400 text-sm font-medium">TestimonialWall</span>
          </div>
          <div className="flex items-center gap-6">
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className="text-gray-500 hover:text-gray-300 text-sm transition">{tab}</button>
            ))}
          </div>
          <button onClick={() => navigate('/login')} className="text-gray-500 hover:text-gray-300 text-sm transition">Sign In</button>
        </div>
        <p className="text-center text-gray-600 text-xs mt-4">© {new Date().getFullYear()} TestimonialWall. All rights reserved.</p>
      </footer>
    </div>
  );
}
